import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Play, Upload, Edit, Trash2, Plus } from 'lucide-react';
import { promotionalVideos } from '../../mockData';
import { useToast } from '../../hooks/use-toast';

const AdminMarketing = () => {
  const { toast } = useToast();
  const [videos, setVideos] = useState(promotionalVideos);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    category: '',
    file: null
  });

  const handleUpload = (e) => {
    e.preventDefault();
    toast({
      title: 'Video Uploaded',
      description: `${newVideo.title} has been uploaded successfully.`
    });
    setShowUploadForm(false);
    setNewVideo({ title: '', category: '', file: null });
  };

  const handleDelete = (videoId) => {
    toast({
      title: 'Video Deleted',
      description: 'The promotional video has been removed.'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketing & Videos</h1>
          <p className="text-gray-600 mt-2">Manage promotional content and marketing materials</p>
        </div>
        <Button
          className=""
          onClick={() => setShowUploadForm(!showUploadForm)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Upload Video
        </Button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <Card>
          <CardHeader>
            <CardTitle>Upload New Video</CardTitle>
            <CardDescription>Add promotional videos to your marketing library</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label htmlFor="video-title">Video Title</Label>
                <Input
                  id="video-title"
                  placeholder="Enter video title"
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                  required
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="video-category">Category</Label>
                <Input
                  id="video-category"
                  placeholder="e.g., Platform Overview, Case Study"
                  value={newVideo.category}
                  onChange={(e) => setNewVideo({ ...newVideo, category: e.target.value })}
                  required
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="video-file">Video File</Label>
                <div className="mt-2 flex items-center justify-center w-full">
                  <label
                    htmlFor="video-file"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">MP4, MOV, or AVI (MAX. 500MB)</p>
                    </div>
                    <input
                      id="video-file"
                      type="file"
                      className="hidden"
                      accept="video/*"
                      onChange={(e) => setNewVideo({ ...newVideo, file: e.target.files[0] })}
                    />
                  </label>
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit">
                  Upload Video
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowUploadForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Video Library */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <Card key={video.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative aspect-video bg-gray-900 group">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-40">
                <Button size="lg" className="rounded-full w-16 h-16 bg-white hover:bg-gray-100">
                  <Play className="h-8 w-8 text-gray-900" />
                </Button>
              </div>
              <div className="absolute top-3 right-3 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                {video.duration}
              </div>
            </div>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg">{video.title}</CardTitle>
                  <CardDescription className="mt-1">{video.category}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => handleDelete(video.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> Video hosting is currently mocked. In production, videos will be hosted on your CDN or video platform (e.g., Vimeo, Wistia) for optimal performance.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMarketing;